import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Download, Save, Share2, Copy, X, Mail, Send, Trash2, Eye, Plus,
  ExternalLink, PlayCircle, PauseCircle, ShieldAlert, Sparkles, AlertTriangle,
} from "lucide-react";

const LABELS = {
  executive_summary: "Executive Summary",
  target_population: "Target Population",
  housing_model: "Recommended Housing Model",
  program_alignment: "Program Alignment & Referral Flow",
  regulatory: "Regulatory / Property Requirements",
  risks: "Risks & Assumptions (narrative)",
  exit_strategy: "Exit Strategy",
};

const ROADMAP = [
  { k: "validate", l: "Validate" }, { k: "partner", l: "Partner" },
  { k: "underwrite", l: "Final Underwriting" }, { k: "contract", l: "Contract / Acquire" },
  { k: "prepare", l: "Prepare Property" }, { k: "compliance", l: "Compliance / Inspection" },
  { k: "activate", l: "Activate Referral" }, { k: "place", l: "Place Residents" },
  { k: "stabilize", l: "Stabilize" }, { k: "operate", l: "Operate" },
];

const RISK_CATEGORIES = ["Regulatory", "Demand", "Funding", "Operator", "Property", "Conversion", "Financial", "Operational", "Exit"];

export default function PacketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pk, setPk] = useState(null);
  const [prop, setProp] = useState(null);
  const [gate, setGate] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [expiresDays, setExpiresDays] = useState(7);
  const [shareUrl, setShareUrl] = useState("");
  const [shareExpiresAt, setShareExpiresAt] = useState("");

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  const load = async () => {
    const { data } = await api.get(`/packets/${id}`);
    data.unresolved_items ||= [];
    data.verified_items ||= [];
    data.assumed_items ||= [];
    data.risks ||= [];
    data.exit_options ||= [];
    data.sources ||= [];
    data.roadmap_stage ||= "validate";
    data.stage ||= "opportunity";
    data.share_status ||= "draft";
    data.packet_type ||= "conceptual";
    setPk(data);
    if (data.share_token) {
      setShareUrl(`${window.location.origin}/share/packet/${data.share_token}`);
      setShareExpiresAt(data.share_expires_at || "");
    } else { setShareUrl(""); setShareExpiresAt(""); }
    if (data.property_id) {
      try { const r = await api.get(`/properties/${data.property_id}`); setProp(r.data); } catch {}
    }
    try { const g = await api.get(`/packets/${id}/gate`); setGate(g.data); } catch {}
  };

  const markReady = async () => {
    try {
      await api.post(`/packets/${id}/mark-ready`);
      toast.success("Packet marked Ready-to-Share");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "object" && detail?.status) {
        toast.error(`Blocked — unverified: ${(detail.status.unverified_fields || []).concat(detail.status.missing_fields || []).join(", ") || "critical facts"}`);
      } else {
        toast.error(detail?.message || detail || "Cannot mark ready");
      }
    }
  };

  const aiDraft = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/packets/${id}/ai-draft`, { program_id: pk.program_id || "" });
      const d = data.draft || {};
      setPk(cur => ({
        ...cur,
        thesis: d.thesis || cur.thesis,
        verified_items: [...(cur.verified_items || []), ...(d.verified_items || [])],
        assumed_items: [...(cur.assumed_items || []), ...(d.assumed_items || [])],
        unresolved_items: [...(cur.unresolved_items || []), ...(d.unresolved_items || [])],
        risks: [...(cur.risks || []), ...(d.risks || [])],
        exit_options: [...(cur.exit_options || []), ...(d.exit_options || [])],
      }));
      toast.success("AI draft appended · Review, edit, then click Save. Nothing was auto-promoted to Verified.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI draft failed");
    } finally { setAiBusy(false); }
  };

  const createShare = async () => {
    try {
      const { data } = await api.post(`/packets/${id}/share`, { expires_days: Number(expiresDays) });
      const url = `${window.location.origin}/share/packet/${data.share_token}`;
      setShareUrl(url); setShareExpiresAt(data.share_expires_at);
      toast.success(`Share link created — expires ${new Date(data.share_expires_at).toLocaleDateString()}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) ? detail.message
        : (typeof detail === "string" ? detail : "Could not create share link. Please try again.");
      toast.error(msg);
    }
  };
  const revokeShare = async () => {
    await api.post(`/packets/${id}/unshare`);
    setShareUrl(""); setShareExpiresAt("");
    toast.success("Share link revoked");
  };
  const copyShare = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied to clipboard"); };

  const [recipientEmail, setRecipientEmail] = useState("");
  const [addingRecipient, setAddingRecipient] = useState(false);
  const addRecipient = async () => {
    if (!recipientEmail.trim()) return;
    setAddingRecipient(true);
    try {
      await api.post(`/packets/${id}/recipients`, { email: recipientEmail.trim() });
      toast.success(`Added ${recipientEmail}`);
      setRecipientEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add recipient");
    } finally { setAddingRecipient(false); }
  };
  const removeRecipient = async (email) => {
    await api.delete(`/packets/${id}/recipients/${encodeURIComponent(email)}`);
    load();
  };
  const sendDigestNow = async () => {
    try {
      const r = await api.post(`/packets/${id}/send-digest-now`);
      toast.success(`Digest queued to ${r.data.recipients} recipient(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send digest");
    }
  };

  const persist = async (patch = {}) => {
    await api.put(`/packets/${id}`, {
      property_id: pk.property_id, program_id: pk.program_id || "",
      packet_type: pk.packet_type || "conceptual",
      title: pk.title, sections: pk.sections,
      thesis: pk.thesis || "",
      unresolved_items: pk.unresolved_items || [],
      verified_items: pk.verified_items || [],
      assumed_items: pk.assumed_items || [],
      risks: pk.risks || [],
      roadmap_stage: pk.roadmap_stage || "validate",
      stage: pk.stage || "opportunity",
      exit_options: pk.exit_options || [],
      sources: pk.sources || [],
      performance: pk.performance || {},
      uw_inputs: pk.uw_inputs || {},
      ...patch,
    });
  };

  const save = async () => {
    await persist();
    toast.success("Packet saved");
  };

  const changePacketType = async (t) => {
    setPk((cur) => ({ ...cur, packet_type: t }));
    try {
      await persist({ packet_type: t });
      toast.success(t === "conceptual" ? "Marked Conceptual — shareable now with labeled facts" : "Marked Investor-ready — requires verified facts to share");
      load();
    } catch {
      toast.error("Could not update packet type");
    }
  };

  const downloadPdf = async () => {
    const res = await fetch(`${API}/packets/${id}/pdf`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${localStorage.getItem("sahoi_token") || ""}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${pk.title}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const toggleStage = () => {
    setPk({ ...pk, stage: pk.stage === "operational" ? "opportunity" : "operational" });
  };

  if (!pk) return <div>Loading…</div>;
  const isOperational = pk.stage === "operational";

  return (
    <div className="space-y-6 max-w-5xl mx-auto" data-testid="packet-detail-page">
      <div className="flex justify-between items-start gap-4">
        <div className="min-w-0 flex-1">
          <Link to="/packets" className="text-xs text-slate-500 hover:text-slate-900">← All Packets</Link>
          <div className="flex items-center gap-2 mt-1">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Internal Opportunity Workspace</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${isOperational ? "bg-emerald-100 text-emerald-800" : "bg-slate-900 text-white"}`} data-testid="stage-pill">
              {isOperational ? "Operational" : "Opportunity"}
            </span>
          </div>
          <input
            className="text-3xl font-bold text-slate-900 mt-1 bg-transparent outline-none border-b border-transparent focus:border-slate-300 w-full"
            value={pk.title} onChange={e=>setPk({...pk, title:e.target.value})}
            data-testid="packet-title-input"
          />
          {prop && <div className="text-sm text-slate-500 mt-1">{prop.address}, {prop.city} · {prop.county} County</div>}
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap justify-end">
          <Button variant="outline" onClick={aiDraft} disabled={aiBusy} data-testid="ai-draft-btn">
            <Sparkles className="w-4 h-4 mr-1"/> {aiBusy ? "Drafting…" : "AI Draft"}
          </Button>
          <Button variant="outline" onClick={()=>navigate(`/packets/${id}/preview`)} data-testid="packet-preview-btn">
            <Eye className="w-4 h-4 mr-1"/> Preview External Report
          </Button>
          <Dialog open={shareOpen} onOpenChange={setShareOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="packet-share-btn"><Share2 className="w-4 h-4 mr-1"/>Share</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Shareable Deal Room Link</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-500">Anyone with the link can view this report in read-only mode until it expires. No account required.</p>
              {shareUrl ? (
                <div className="space-y-3">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-xs font-mono break-all" data-testid="share-url">{shareUrl}</div>
                  <div className="text-xs text-slate-600">Expires: {shareExpiresAt ? new Date(shareExpiresAt).toLocaleString() : "—"}</div>
                  <div className="flex gap-2">
                    <Button onClick={copyShare} className="bg-slate-900 hover:bg-slate-800 flex-1" data-testid="share-copy-btn"><Copy className="w-4 h-4 mr-1"/>Copy link</Button>
                    <Button onClick={revokeShare} variant="outline" data-testid="share-revoke-btn"><X className="w-4 h-4 mr-1"/>Revoke</Button>
                  </div>
                  <div className="pt-4 border-t border-slate-200 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Mail className="w-4 h-4" /> Deal Room Recipients</div>
                    <div className="text-[11px] text-slate-500">Recipients get a weekly digest of new ≥80-confidence matches for this property, plus you can trigger a manual send.</div>
                    <div className="flex gap-2">
                      <Input type="email" placeholder="investor@example.com" value={recipientEmail} onChange={e=>setRecipientEmail(e.target.value)} data-testid="recipient-email-input" />
                      <Button onClick={addRecipient} disabled={addingRecipient} data-testid="add-recipient-btn" className="bg-slate-900 hover:bg-slate-800">Add</Button>
                    </div>
                    {(pk.recipients || []).length > 0 && (
                      <div className="space-y-1">
                        {pk.recipients.map(r => (
                          <div key={r.email} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1.5" data-testid={`recipient-row-${r.email}`}>
                            <span className="truncate">{r.email}</span>
                            <button onClick={()=>removeRecipient(r.email)} className="text-rose-600 hover:text-rose-800"><Trash2 className="w-3.5 h-3.5"/></button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={sendDigestNow} className="w-full" data-testid="send-digest-btn"><Send className="w-3.5 h-3.5 mr-1"/>Send digest now</Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <Label>Expires in (days)</Label>
                    <Input type="number" min={1} max={90} value={expiresDays} onChange={e=>setExpiresDays(e.target.value)} data-testid="share-days-input"/>
                  </div>
                  <Button onClick={createShare} className="bg-slate-900 hover:bg-slate-800 w-full" data-testid="share-create-btn"><Share2 className="w-4 h-4 mr-1"/>Create link</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={save} data-testid="packet-save-btn"><Save className="w-4 h-4 mr-1"/>Save</Button>
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={downloadPdf} data-testid="packet-pdf-btn"><Download className="w-4 h-4 mr-1"/>Export PDF</Button>
        </div>
      </div>

      {/* Packet type & conceptual banner */}
      <Card className="p-4 border-slate-200" data-testid="packet-type-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Packet type</div>
            <div className="text-sm text-slate-700 mt-0.5">
              {pk.packet_type === "investor_ready"
                ? "Investor-ready — every critical fact must be verified before this can be shared."
                : "Conceptual — shareable now with unverified facts clearly labeled by evidence level and source."}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant={pk.packet_type !== "investor_ready" ? "default" : "outline"}
              className={pk.packet_type !== "investor_ready" ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
              onClick={() => changePacketType("conceptual")}
              data-testid="type-conceptual-btn"
            >Conceptual</Button>
            <Button
              variant={pk.packet_type === "investor_ready" ? "default" : "outline"}
              className={pk.packet_type === "investor_ready" ? "bg-slate-900 hover:bg-slate-800" : ""}
              onClick={() => changePacketType("investor_ready")}
              data-testid="type-investor-btn"
            >Investor-ready</Button>
          </div>
        </div>
        {pk.packet_type !== "investor_ready" && (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="conceptual-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" /> Conceptual packet — not for investment reliance. The report shows a visible "Conceptual" header and labels every unverified figure.
          </div>
        )}
      </Card>

      {/* Ready-to-Share gate (investor-ready packets only) */}
      {gate && pk.packet_type === "investor_ready" && (
        <Card className={`p-4 border-2 ${gate.share_status === "ready_to_share" ? "border-emerald-300 bg-emerald-50/40" : "border-rose-300 bg-rose-50/30"}`} data-testid="ready-to-share-gate">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <ShieldAlert className={`w-5 h-5 ${gate.share_status === "ready_to_share" ? "text-emerald-700" : "text-rose-700"}`} />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Verification checkpoint</div>
                <div className="text-sm font-semibold">
                  {gate.share_status === "ready_to_share" ? "Ready-to-Share ✓" : "Blocked — critical facts unverified"}
                  <span className="ml-2 text-slate-500 font-normal">({gate.verification.verified_count}/{gate.verification.total} verified{gate.verification.conflict_count > 0 && <span className="text-rose-600"> · {gate.verification.conflict_count} conflict</span>})</span>
                </div>
                {gate.stale && <div className="text-xs text-amber-800 flex items-center gap-1 mt-1"><AlertTriangle className="w-3.5 h-3.5" /> {gate.stale_reason}</div>}
                {!gate.verification.ready_to_share && (
                  <div className="text-xs text-rose-700 mt-1">Unverified: {(gate.verification.unverified_fields || []).concat(gate.verification.missing_fields || []).join(", ") || "—"}</div>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {prop && <Button variant="outline" onClick={() => navigate(`/properties/${prop.id}`)} data-testid="gate-open-property-btn">Open property record</Button>}
              <Button onClick={markReady} disabled={!gate.verification.ready_to_share || gate.share_status === "ready_to_share"} className="bg-slate-900 hover:bg-slate-800 disabled:opacity-40" data-testid="mark-ready-btn">
                {gate.share_status === "ready_to_share" ? "Ready ✓" : "Mark Ready to Share"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Thesis + Stage */}
      <Card className="p-6 border-slate-200 bg-white">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Opportunity thesis (appears at the top of the external report)</div>
        <Textarea rows={3} value={pk.thesis || ""} onChange={e=>setPk({...pk, thesis: e.target.value})} placeholder="One-paragraph thesis: why this property + this program + this moment." data-testid="packet-thesis-input" />

        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Roadmap stage</Label>
            <select
              value={pk.roadmap_stage} onChange={e=>setPk({...pk, roadmap_stage: e.target.value})}
              className="w-full mt-1 border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="packet-roadmap-select"
            >
              {ROADMAP.map(r => <option key={r.k} value={r.k}>{r.l}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Stage</Label>
            <Button variant="outline" onClick={toggleStage} className="mt-1 w-full justify-start" data-testid="packet-stage-toggle">
              {isOperational
                ? <><PauseCircle className="w-4 h-4 mr-2 text-emerald-600"/>Operational — show performance in external report</>
                : <><PlayCircle className="w-4 h-4 mr-2"/>Opportunity — pre-deployment</>}
            </Button>
          </div>
        </div>
      </Card>

      {/* Evidence lists */}
      <div className="grid md:grid-cols-3 gap-4" data-testid="packet-evidence-editor">
        <ListEditor title="Verified facts" tone="emerald" items={pk.verified_items} onChange={(v)=>setPk({...pk, verified_items:v})} placeholder="e.g., HUD-VASH partner confirmed by SAHA coordinator on 2025-11-14" testid="verified" />
        <ListEditor title="Assumptions / estimates" tone="amber" items={pk.assumed_items} onChange={(v)=>setPk({...pk, assumed_items:v})} placeholder="e.g., specialized rent projection based on SAFMR" testid="assumed" />
        <ListEditor title="Needs verification" tone="rose" items={pk.unresolved_items} onChange={(v)=>setPk({...pk, unresolved_items:v})} placeholder="e.g., zoning letter pending" testid="unresolved" />
      </div>

      {/* Risks editor */}
      <Card className="p-6 border-slate-200 bg-white" data-testid="packet-risks-editor">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Risks &amp; mitigation</div>
          <Button size="sm" variant="outline" onClick={()=>setPk({...pk, risks:[...(pk.risks||[]), {category:"Regulatory", description:"", mitigation:""}]})} data-testid="add-risk-btn">
            <Plus className="w-3.5 h-3.5 mr-1"/> Add risk
          </Button>
        </div>
        {(pk.risks || []).length === 0 ? (
          <div className="text-xs text-slate-500 italic">If empty, the external report will populate a default set of categorized risks.</div>
        ) : (
          <div className="space-y-2">
            {pk.risks.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2" data-testid={`risk-row-${i}`}>
                <select value={r.category} onChange={e=>{ const arr=[...pk.risks]; arr[i]={...arr[i], category:e.target.value}; setPk({...pk, risks:arr}); }}
                        className="col-span-2 border border-slate-200 rounded-md px-2 text-xs bg-white">
                  {RISK_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
                <Input placeholder="Risk description" value={r.description} onChange={e=>{ const arr=[...pk.risks]; arr[i]={...arr[i], description:e.target.value}; setPk({...pk, risks:arr}); }} className="col-span-5 text-xs" />
                <Input placeholder="Mitigation" value={r.mitigation} onChange={e=>{ const arr=[...pk.risks]; arr[i]={...arr[i], mitigation:e.target.value}; setPk({...pk, risks:arr}); }} className="col-span-4 text-xs" />
                <button className="col-span-1 text-rose-600 hover:text-rose-800" onClick={()=>setPk({...pk, risks: pk.risks.filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4"/></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Exit options + Sources */}
      <div className="grid md:grid-cols-2 gap-4">
        <ListEditor title="Exit &amp; alternative uses" tone="slate" items={pk.exit_options} onChange={(v)=>setPk({...pk, exit_options:v})} placeholder="e.g., Sell as owner-occupied single family" testid="exit-options" />
        <Card className="p-5 border-slate-200 bg-white" data-testid="packet-sources-editor">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Sources &amp; due diligence</div>
            <Button size="sm" variant="outline" onClick={()=>setPk({...pk, sources:[...(pk.sources||[]), {label:"", url:"", date: new Date().toISOString().slice(0,10)}]})} data-testid="add-source-btn">
              <Plus className="w-3.5 h-3.5 mr-1"/> Add source
            </Button>
          </div>
          {(pk.sources || []).length === 0 ? (
            <div className="text-xs text-slate-500 italic">Add clickable citations (public sources, partner confirmations, conversation logs).</div>
          ) : (
            <div className="space-y-2">
              {pk.sources.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`source-row-${i}`}>
                  <Input placeholder="Label" value={s.label} onChange={e=>{ const arr=[...pk.sources]; arr[i]={...arr[i], label:e.target.value}; setPk({...pk, sources:arr}); }} className="col-span-4 text-xs" />
                  <Input placeholder="https://…" value={s.url} onChange={e=>{ const arr=[...pk.sources]; arr[i]={...arr[i], url:e.target.value}; setPk({...pk, sources:arr}); }} className="col-span-5 text-xs" />
                  <Input placeholder="YYYY-MM-DD" value={s.date} onChange={e=>{ const arr=[...pk.sources]; arr[i]={...arr[i], date:e.target.value}; setPk({...pk, sources:arr}); }} className="col-span-2 text-xs" />
                  <button className="col-span-1 text-rose-600 hover:text-rose-800" onClick={()=>setPk({...pk, sources: pk.sources.filter((_,j)=>j!==i)})}><Trash2 className="w-4 h-4"/></button>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Performance (operational only) */}
      {isOperational && (
        <Card className="p-6 border-slate-200 bg-white" data-testid="packet-performance-editor">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Performance (operational)</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <PerfInput label="Occupancy %" val={pk.performance?.occupancy_pct} onChange={v=>setPk({...pk, performance:{...(pk.performance||{}), occupancy_pct:v}})} testid="perf-occupancy" />
            <PerfInput label="Placements" val={pk.performance?.placements} onChange={v=>setPk({...pk, performance:{...(pk.performance||{}), placements:v}})} testid="perf-placements" />
            <PerfInput label="Actual revenue ($)" val={pk.performance?.revenue} onChange={v=>setPk({...pk, performance:{...(pk.performance||{}), revenue:v}})} testid="perf-revenue" />
            <PerfInput label="Actual NOI ($)" val={pk.performance?.noi} onChange={v=>setPk({...pk, performance:{...(pk.performance||{}), noi:v}})} testid="perf-noi" />
          </div>
          <div className="text-[11px] text-slate-500 mt-3 italic">Detailed reporting periods, updates and documents can be added in a follow-up release. This scaffolding is visible on the external report.</div>
        </Card>
      )}

      {/* Existing narrative sections */}
      <Card className="p-6 border-slate-200 bg-white">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-4">Narrative sections (feed the external report)</div>
        {Object.entries(pk.sections || {}).map(([k, v]) => (
          <div key={k} className="mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-2">{LABELS[k] || k}</h2>
            <Textarea rows={4} value={v}
              onChange={e => setPk({...pk, sections: {...pk.sections, [k]: e.target.value}})}
              className="text-sm leading-relaxed"
              data-testid={`packet-section-${k}`}
            />
          </div>
        ))}
        <div className="text-[11px] text-slate-500 pt-4 border-t border-slate-100">
          This workspace is internal. The external report is polished, read-only and appears at the shared deal-room link. Preview it above.
        </div>
      </Card>
    </div>
  );
}

/* ---------- Editors ---------- */
function ListEditor({ title, tone, items = [], onChange, placeholder, testid }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber: "border-amber-200 bg-amber-50/40",
    rose: "border-rose-200 bg-rose-50/40",
    slate: "border-slate-200 bg-white",
  };
  const [draft, setDraft] = useState("");
  const add = () => {
    if (!draft.trim()) return;
    onChange([...(items||[]), draft.trim()]);
    setDraft("");
  };
  return (
    <Card className={`p-5 border ${tones[tone]}`} data-testid={`list-editor-${testid}`}>
      <div className="text-xs uppercase tracking-widest text-slate-700 font-semibold mb-2" dangerouslySetInnerHTML={{__html: title}} />
      <div className="flex gap-2">
        <Input placeholder={placeholder} value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); add(); } }} className="text-xs" data-testid={`${testid}-input`} />
        <Button size="sm" onClick={add} className="bg-slate-900 hover:bg-slate-800 shrink-0" data-testid={`${testid}-add`}>Add</Button>
      </div>
      <ul className="mt-3 space-y-1">
        {(items||[]).map((x, i) => (
          <li key={i} className="flex items-start justify-between gap-2 text-xs text-slate-800 bg-white/80 rounded px-2 py-1">
            <span>{x}</span>
            <button onClick={()=>onChange(items.filter((_,j)=>j!==i))} className="text-rose-600 hover:text-rose-800 shrink-0" data-testid={`${testid}-remove-${i}`}><Trash2 className="w-3.5 h-3.5"/></button>
          </li>
        ))}
        {(items||[]).length === 0 && <li className="text-[11px] italic text-slate-500">None yet.</li>}
      </ul>
    </Card>
  );
}

function PerfInput({ label, val, onChange, testid }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</Label>
      <Input type="number" value={val ?? ""} onChange={e=>onChange(e.target.value === "" ? null : Number(e.target.value))} className="font-mono text-sm" data-testid={testid} />
    </div>
  );
}
